import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const getFocusableElements = (container) => {
  if (!container) return [];

  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
    const isVisible = element.offsetWidth > 0
      || element.offsetHeight > 0
      || element.getClientRects().length > 0;

    return isVisible && !element.getAttribute('aria-hidden');
  });
};

function useModalA11y({
  isOpen,
  dialogRef,
  onClose,
  closeOnEscape = true,
  trapFocus = true,
  returnFocus = true,
  lockBodyScroll = false,
}) {
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const dialog = dialogRef.current;
    const previousActiveElement = document.activeElement;
    const originalBodyOverflow = document.body.style.overflow;
    const firstFocusable = getFocusableElements(dialog)[0];

    if (lockBodyScroll) {
      document.body.style.overflow = 'hidden';
    }

    (firstFocusable || dialog)?.focus?.();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && closeOnEscape && typeof onCloseRef.current === 'function') {
        event.preventDefault();
        onCloseRef.current(event);
        return;
      }

      if (event.key !== 'Tab' || !trapFocus || !dialog) return;

      const focusableElements = getFocusableElements(dialog);
      if (focusableElements.length === 0) {
        event.preventDefault();
        dialog.focus?.();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (event.shiftKey && (activeElement === firstElement || !dialog.contains(activeElement))) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (lockBodyScroll) {
        document.body.style.overflow = originalBodyOverflow;
      }
      if (returnFocus && previousActiveElement?.isConnected) {
        previousActiveElement.focus?.();
      }
    };
  }, [isOpen, dialogRef, closeOnEscape, trapFocus, returnFocus, lockBodyScroll]);
}

export default useModalA11y;
